/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {DashboardSelectorEl} from 'common/widgets/dashboard-selector';

import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import {Section, SubSection} from './utils';

export function DashboardSelectorSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Board Selector'>
      <SubSection title='mp-dashboard-selector' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`dashboardSelector`)}}
          >
            Open board selector
          </ButtonEl>
          <DashboardSelectorEl
            attrs={{open: designSystem.state.open.dashboardSelector}}
            on={{
              select: designSystem.helpers.handleDashboardSelectorSelect,
              menuchange: (ev) => designSystem.helpers.handleMenuChange(`dashboardSelector`, ev.detail.state),
            }}
            props={{controller: designSystem.state.dashboardController}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`restrictCreatingDashboard`)}}
          >
            Open create board disabled
          </ButtonEl>
          <DashboardSelectorEl
            attrs={{
              open: designSystem.state.open.restrictCreatingDashboard,
              'disable-create-dashboard': true,
            }}
            on={{
              select: designSystem.helpers.handleDashboardSelectorSelect,
              menuchange: (ev) => designSystem.helpers.handleMenuChange(`restrictCreatingDashboard`, ev.detail.state),
            }}
            props={{controller: designSystem.state.dashboardController}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`creatorNameDashboardSelector`)}}
          >
            Open board selector with creator name as sublabel
          </ButtonEl>
          <DashboardSelectorEl
            attrs={{
              open: designSystem.state.open.creatorNameDashboardSelector,
              'show-creator-sublabel': true,
            }}
            on={{
              select: designSystem.helpers.handleDashboardSelectorSelect,
              menuchange: (ev) =>
                designSystem.helpers.handleMenuChange(`creatorNameDashboardSelector`, ev.detail.state),
            }}
            props={{controller: designSystem.state.dashboardController}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`singleSectionDashboardSelector`)}}
          >
            Open board selector with a single section
          </ButtonEl>
          <DashboardSelectorEl
            attrs={{open: designSystem.state.open.singleSectionDashboardSelector, 'single-section': true}}
            on={{
              select: designSystem.helpers.handleDashboardSelectorSelect,
              menuchange: (ev) =>
                designSystem.helpers.handleMenuChange(`singleSectionDashboardSelector`, ev.detail.state),
            }}
            props={{controller: designSystem.state.dashboardController}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`initiallySortedDashboardSelector`)}}
          >
            Open board selector an initially sorted system
          </ButtonEl>
          <DashboardSelectorEl
            attrs={{open: designSystem.state.open.initiallySortedDashboardSelector}}
            on={{
              select: designSystem.helpers.handleDashboardSelectorSelect,
              menuchange: (ev) =>
                designSystem.helpers.handleMenuChange(`initiallySortedDashboardSelector`, ev.detail.state),
            }}
            props={{controller: designSystem.state.dashboardController, initialSortedDashboardIds: [3, 6, 1, 7]}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`onlyEditableDashboardsSelector`)}}
          >
            Open board selector with only editable boards
          </ButtonEl>
          <DashboardSelectorEl
            attrs={{
              open: designSystem.state.open.onlyEditableDashboardsSelector,
              'display-only-editable-dashboards': true,
            }}
            on={{
              select: designSystem.helpers.handleDashboardSelectorSelect,
              menuchange: (ev) =>
                designSystem.helpers.handleMenuChange(`onlyEditableDashboardsSelector`, ev.detail.state),
            }}
            props={{controller: designSystem.state.dashboardController}}
          />
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
